import React from "react";
import { Grid, Typography, Alert, Chip } from "@mui/material";

import { ROLES } from "../../../constants/roles";
import FileUploadField from "../FileUploadField";
import { useAuth } from "../../../contexts/AuthContext";
import { getEligibleFinanciers } from "../config/emiConfig";
import { useEmiAdminConfig } from "../../../hooks/useEmiAdminConfig";

const DocumentUploadStep = ({ data, onChange, showErrors, allFormData }) => {
  const { getUserRole } = useAuth();
  const role = getUserRole();
  const { data: emiAdminConfig } = useEmiAdminConfig("v2");

  const fileError = (field) =>
    showErrors && !data[field] ? "This document is required" : "";

  const isCibilLocked = Boolean(data.cibilNumber);

  const eligibleFinanciers = getEligibleFinanciers(emiAdminConfig, {
    cibilScore: data.cibilNumber,
    downpayment: allFormData?.driverInformation?.downpayment,
    city: allFormData?.driverInformation?.city,
  });

  const isFieldLocked = (field) => false;

  const showNoFinancierState =
    isCibilLocked && data.cibilNumber && eligibleFinanciers.length === 0;

  if (role === ROLES.DEALER) {
    return (
      <Grid container spacing={2.5}>
        <Grid item xs={12} sm={6}>
          <FileUploadField
            label="Aadhaar Card (Front)"
            required
            value={data.aadhaarFrontUrl}
            onChange={(file) => onChange("aadhaarFrontUrl", file)}
            error={fileError("aadhaarFrontUrl")}
            disabled={isCibilLocked}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <FileUploadField
            label="Aadhaar Card (Back)"
            required
            value={data.aadhaarBackUrl}
            onChange={(file) => onChange("aadhaarBackUrl", file)}
            error={fileError("aadhaarBackUrl")}
            disabled={isCibilLocked}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <FileUploadField
            label="PAN Card"
            required
            value={data.panCardUrl}
            onChange={(file) => onChange("panCardUrl", file)}
            error={fileError("panCardUrl")}
            disabled={isCibilLocked}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <FileUploadField
            label="Address Proof"
            required
            value={data.addressProofUrl}
            onChange={(file) => onChange("addressProofUrl", file)}
            error={fileError("addressProofUrl")}
            disabled={isFieldLocked("addressProofUrl")}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <FileUploadField
            label="RC (Front)"
            required
            value={data.rcFrontUrl}
            onChange={(file) => onChange("rcFrontUrl", file)}
            error={fileError("rcFrontUrl")}
            disabled={isFieldLocked("rcFrontUrl")}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <FileUploadField
            label="RC (Back)"
            required
            value={data.rcBackUrl}
            onChange={(file) => onChange("rcBackUrl", file)}
            error={fileError("rcBackUrl")}
            disabled={isFieldLocked("rcBackUrl")}
          />
        </Grid>

        <Grid item xs={12}>
          {isCibilLocked ? (
            <Alert severity="success">
              CIBIL score received: {data.cibilNumber}. You can proceed to
              select a financier.
            </Alert>
          ) : (
            <Alert severity="info">
              These documents will be reviewed by the Ops team, who will enter
              the CIBIL score here once the bureau check is complete.
            </Alert>
          )}
        </Grid>
      </Grid>
    );
  }

  return (
    <Grid container spacing={2.5}>
      <Grid item xs={12} sm={6}>
        <FileUploadField
          label="Aadhaar Card (Front)"
          value={data.aadhaarFrontUrl}
          readOnly
        />
      </Grid>

      <Grid item xs={12} sm={6}>
        <FileUploadField
          label="Aadhaar Card (Back)"
          value={data.aadhaarBackUrl}
          readOnly
        />
      </Grid>

      <Grid item xs={12} sm={6}>
        <FileUploadField label="PAN Card" value={data.panCardUrl} readOnly />
      </Grid>

      <Grid item xs={12} sm={6}>
        <FileUploadField
          label="Address Proof"
          value={data.addressProofUrl}
          readOnly
        />
      </Grid>

      {/* <Grid item xs={12} sm={6}>
        <FileUploadField label="RC" value={data.rcUrl} readOnly />
      </Grid> */}

      <Grid item xs={12} sm={6}>
        <FileUploadField label="RC (Front)" value={data.rcFrontUrl} readOnly />
      </Grid>

      <Grid item xs={12} sm={6}>
        <FileUploadField label="RC (Back)" value={data.rcBackUrl} readOnly />
      </Grid>

      {isCibilLocked && (
        <Grid item xs={12}>
          <Typography sx={{ fontSize: 12, color: "#66766F" }}>
            Entered by {data.enteredBy || "—"} on{" "}
            {data.enteredAt ? new Date(data.enteredAt).toLocaleString() : "—"}
          </Typography>
        </Grid>
      )}

      {data.cibilNumber && !showNoFinancierState && (
        <Grid item xs={12}>
          <Typography sx={{ fontSize: 13, fontWeight: 600, mb: 1 }}>
            Eligible Financiers
          </Typography>
          {eligibleFinanciers.map((financier) => (
            <Chip
              key={financier.code}
              label={`${financier.displayName} (${Math.round(financier.interestRate * 100)}% ROI)`}
              sx={{ mr: 1, mb: 1 }}
            />
          ))}
        </Grid>
      )}

      {showNoFinancierState && (
        <Grid item xs={12}>
          <Alert severity="error">
            No financier is available for a CIBIL score of {data.cibilNumber}{" "}
            with the selected downpayment and city.
          </Alert>
        </Grid>
      )}
    </Grid>
  );
};

export default DocumentUploadStep;
